// Browse filters live in the URL query string, which makes the URL the shared
// source of truth between the product list and the map. Both read through this
// parser so a filter can't mean one thing on one surface and something else on
// the other, and so a filtered view is linkable.

export type BrowseFilters = {
  category: string | null;
  fulfillment: "any" | "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  showSoldOut: boolean;
  // Radius in miles from the buyer's location. null = no distance limit.
  withinMiles: number | null;
  ebtOnly: boolean;
  // The search box. It filters every surface, so it belongs in the shared
  // contract rather than living only in whichever panel is on screen — the map
  // was showing every maker while the list beside it was filtered to one.
  q: string;
};

export const DISTANCE_OPTIONS = [5, 10, 25, 50] as const;

// The map is an overlay that owns a pushed history entry, so closing it rewinds
// the URL past any filter it changed. It announces changes on this event too, so
// the browse list can pick them up regardless of what the query string survives.
export const BROWSE_FILTERS_EVENT = "stoop:browse-filters";

export function parseBrowseFilters(search: string): BrowseFilters {
  const p = new URLSearchParams(search);
  const fulfillment = p.get("fulfillment");
  const within = Number(p.get("within"));
  return {
    category: p.get("category"),
    fulfillment:
      fulfillment === "seller_pickup" ||
      fulfillment === "drop_zone_pickup" ||
      fulfillment === "local_delivery"
        ? fulfillment
        : "any",
    // Absent means show them; only an explicit "0" hides sold-out items.
    showSoldOut: p.get("showSoldOut") !== "0",
    withinMiles: Number.isFinite(within) && within > 0 ? within : null,
    ebtOnly: p.get("ebt") === "1",
    q: (p.get("q") ?? "").trim(),
  };
}

export function hasActiveFilters(f: BrowseFilters): boolean {
  return (
    f.category != null ||
    f.fulfillment !== "any" ||
    !f.showSoldOut ||
    f.withinMiles != null ||
    f.ebtOnly ||
    f.q !== ""
  );
}
